// Task chat slide-in panel, shared by the coach Task Tracker (TaskTracker.js) and the admin
// Task Tracker (TaskManagement/taskmanagement.js). "Chat" reuses the task's existing
// comments thread/endpoints.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Send, X } from 'lucide-react';
import { addComment, getAssignableTaskUsers, getTaskBytaskId, markTaskMentionsSeen } from '../../api';
import MentionTextarea, { MentionText, mentionsAll } from '../common/MentionTextarea';
import showToast from '../../utils/toast';
import './TaskChatPanel.css';

const formatChatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

const ROLE_LABELS = {
    admin: 'Admin', coach: 'Coach', 'sports-coach': 'Sports Coach', 'music-coach': 'Music Coach',
    'purchase-manager': 'Purchase Manager', 'medical-incharge': 'Medical Incharge',
    'balagruha-incharge': 'Balagruha Incharge', amma: 'Amma', student: 'Student',
};

// People who can be @tagged: everyone the current user can assign tasks to (staff + students),
// loaded once per page and shared by every chat panel opened.
let taggablePeoplePromise = null;
const loadTaggablePeople = () => {
    if (!taggablePeoplePromise) {
        taggablePeoplePromise = getAssignableTaskUsers()
            .then((res) => {
                const seen = new Set();
                return [...(res?.data?.staff || []), ...(res?.data?.students || [])]
                    .filter((u) => u?._id && u.name && !seen.has(u._id) && seen.add(u._id))
                    .map((u) => ({ id: u._id, name: u.name.trim(), subtitle: ROLE_LABELS[u.role] || u.role || '' }));
            })
            .catch(() => { taggablePeoplePromise = null; return []; });
    }
    return taggablePeoplePromise;
};

// True when someone @tagged the current user in this task's chat and they haven't opened it since.
export const hasUnseenMention = (task, userId = localStorage.getItem('userId')) => {
    if (!userId) return false;
    const has = (list) => (list || []).some((id) => String(id?._id || id) === String(userId));
    return (task?.comments || []).some((c) => has(c.mentions) && !has(c.mentionsSeenBy));
};

// Returns `tasks` with the current user's mentions on `taskId` marked seen (clears the dot locally).
export const withMentionsSeen = (tasks, taskId, userId = localStorage.getItem('userId')) =>
    tasks.map((t) => (t._id !== taskId ? t : {
        ...t,
        comments: (t.comments || []).map((c) => (
            (c.mentions || []).some((id) => String(id?._id || id) === String(userId))
                ? { ...c, mentionsSeenBy: [...(c.mentionsSeenBy || []), userId] }
                : c
        )),
    }));

// Green dot shown on "View Chat" while the user has an unseen @mention in that chat.
export const MentionDot = () => (
    <span className="task-chat-mention-dot" title="You were mentioned in this chat" aria-label="You were mentioned in this chat" />
);

// `task` only needs { _id, title, comments }; the full thread (with sender names) is loaded on open.
// `onMessageSent` lets the page refresh its list (e.g. the "View Chat (n)" count);
// `onMentionsSeen` lets it clear the chat's green "mentioned" dot once the panel is opened.
function TaskChatPanel({ task, onClose, onMessageSent, onMentionsSeen }) {
    const currentUserId = localStorage.getItem('userId');
    const [chatTask, setChatTask] = useState({ _id: task._id, title: task.title, comments: task.comments || [] });
    const [chatLoading, setChatLoading] = useState(true);
    const [chatDraft, setChatDraft] = useState('');
    const [chatSending, setChatSending] = useState(false);
    const messagesRef = useRef(null);
    const [people, setPeople] = useState([]);
    // Ids of people picked from the @ list for the current draft (names alone can be ambiguous).
    const pickedRef = useRef(new Map());

    // Opening the chat counts as seeing any @mentions of the current user in it.
    useEffect(() => {
        if (!hasUnseenMention(task, currentUserId)) return;
        markTaskMentionsSeen(task._id)
            .then(() => { if (onMentionsSeen) onMentionsSeen(task._id); })
            .catch(() => { /* the dot simply stays until the next open */ });
    }, [task._id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let cancelled = false;
        loadTaggablePeople().then((list) => { if (!cancelled) setPeople(list); });
        return () => { cancelled = true; };
    }, []);

    // Message senders can be tagged/highlighted too, even if they aren't in the assignable list.
    const taggable = useMemo(() => {
        const ids = new Set(people.map((p) => p.id));
        const senders = (chatTask.comments || [])
            .map((c) => c.user)
            .filter((u) => u && u._id && u.name && !ids.has(u._id) && ids.add(u._id))
            .map((u) => ({ id: u._id, name: u.name.trim(), subtitle: ROLE_LABELS[u.role] || '' }));
        return [...people, ...senders];
    }, [people, chatTask.comments]);

    useEffect(() => {
        let cancelled = false;
        setChatTask({ _id: task._id, title: task.title, comments: task.comments || [] });
        setChatLoading(true);
        getTaskBytaskId(task._id)
            .then((full) => { if (!cancelled && full) setChatTask(full); })
            .catch(() => { if (!cancelled) showToast('Failed to load chat', 'error'); })
            .finally(() => { if (!cancelled) setChatLoading(false); });
        return () => { cancelled = true; };
    }, [task._id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the newest message in view.
    useEffect(() => {
        if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }, [chatTask.comments, chatLoading]);

    const sendChatMessage = async () => {
        if (!chatDraft.trim() || chatSending) return;
        setChatSending(true);
        try {
            const text = chatDraft.trim();
            const formData = new FormData();
            formData.append('comments', text);
            // "@all" tags everyone in the @ list; otherwise only people still tagged in the
            // final text (a tag may have been deleted while typing).
            const mentions = (mentionsAll(text)
                ? taggable
                : [...pickedRef.current.values()].filter((p) => text.includes(`@${p.name}`)))
                .map((p) => p.id);
            if (mentions.length) formData.append('mentions', mentions.join(','));
            await addComment(chatTask._id, formData);
            setChatDraft('');
            pickedRef.current = new Map();
            const full = await getTaskBytaskId(chatTask._id);
            if (full) setChatTask(full);
            if (onMessageSent) onMessageSent();
        } catch (error) {
            showToast('Failed to send message', 'error');
        } finally {
            setChatSending(false);
        }
    };

    return (
        <div className="task-tracker-chat-panel">
            <div className="task-tracker-chat-header">
                <div className="task-tracker-chat-header-title">Task: {chatTask.title}</div>
                <button type="button" className="task-tracker-chat-close" onClick={onClose} aria-label="Close chat">
                    <X size={18} />
                </button>
            </div>
            <div className="task-tracker-chat-messages" ref={messagesRef}>
                {chatLoading && <div className="task-tracker-chat-loading">Loading messages…</div>}
                {!chatLoading && (!chatTask.comments || chatTask.comments.length === 0) && (
                    <div className="task-tracker-chat-empty">No messages yet. Say hello!</div>
                )}
                {!chatLoading && (chatTask.comments || []).map((comment, idx) => {
                    const isMine = (comment.user?._id || comment.user) === currentUserId;
                    const senderName = comment.user?.name || (isMine ? 'You' : 'Unknown');
                    return (
                        <div key={comment._id || idx} className={`task-tracker-chat-message ${isMine ? 'mine' : 'theirs'}`}>
                            {!isMine && <div className="task-tracker-chat-avatar">{senderName.charAt(0).toUpperCase()}</div>}
                            <div className="task-tracker-chat-bubble">
                                {!isMine && <div className="task-tracker-chat-sender">{senderName}</div>}
                                <div className="task-tracker-chat-text"><MentionText text={comment.comment} people={taggable} withAll /></div>
                                <div className="task-tracker-chat-time">{formatChatTime(comment.createdAt)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="task-tracker-chat-input">
                <MentionTextarea
                    singleLine
                    inputId="task-tracker-chat-draft"
                    ariaLabel="Type a message. Type @ to tag someone."
                    placeholder="Type a message... (@ to tag)"
                    value={chatDraft}
                    onChange={setChatDraft}
                    onSubmit={sendChatMessage}
                    onPick={(person) => pickedRef.current.set(person.id, person)}
                    people={taggable}
                    allOption
                    allNoun="people"
                    menuPlacement="top"
                    showHint={false}
                    emptyText="No matching person"
                    noPeopleText="Loading people…"
                />
                <button type="button" className="task-tracker-chat-mic" disabled title="Voice messages coming soon">
                    <Mic size={16} />
                </button>
                <button
                    type="button"
                    className="task-tracker-chat-send"
                    onClick={sendChatMessage}
                    disabled={chatSending || !chatDraft.trim()}
                    aria-label="Send message"
                >
                    <Send size={16} />
                </button>
            </div>
        </div>
    );
}

export default TaskChatPanel;
